import {IntlShape} from 'react-intl'
import {textByCount} from '../../../../_gori/utils'
import A1_CONSTANTS from './_const'
import {FORMAT_DATE} from '../../../../_gori/constants'
import moment from 'moment'

const checkJourneyStatus = ({
  statusCurrent,
  statusCheck,
  comparisonType = 'greater_than_or_equal',
}: {
  statusCurrent: string
  statusCheck: string
  comparisonType?: 'less_than' | 'less_than_or_equal' | 'greater_than' | 'greater_than_or_equal'
}) => {
  const arrStatus = Object.values(A1_CONSTANTS.OPTIONS_JOURNEY_STATUS).flatMap(
    (status) => status.value
  )
  if (arrStatus.length === 0) return false
  const foundIndex = arrStatus.indexOf(statusCheck)
  if (foundIndex === -1) return false

  const comparisonRanges = {
    less_than: arrStatus.slice(0, foundIndex),
    less_than_or_equal: arrStatus.slice(0, foundIndex + 1),
    greater_than: arrStatus.slice(foundIndex + 1),
    greater_than_or_equal: arrStatus.slice(foundIndex),
  }
  return comparisonRanges[comparisonType].includes(statusCurrent)
}

export const A1Helpers = {
  checkJourneyStatus,
}

/**
 * Convert a number of hours into an object containing { day, hour, minute }.
 *
 * - Input is a number of hours (can be fractional).
 * - Returns an object that only includes properties with values > 0.
 *
 * @example
 * convertHours(100)
 * // => { day: 4, hour: 4 }
 *
 * @example
 * convertHours(1.25)
 * // => { hour: 1, minute: 15 }
 *
 * @example
 * convertHours(0.25)
 * // => { minute: 15 }
 *
 * @example
 * convertHours(24)
 * // => { day: 1 }
 *
 * @param hours Total hours
 * @returns An object with day, hour, and/or minute fields
 */
export const splitHours = (hours: number): Record<string, number> => {
  const result: Record<string, number> = {}

  const totalMinutes = Math.round(hours * 60)
  const day = Math.floor(totalMinutes / (24 * 60))
  const hour = Math.floor((totalMinutes % (24 * 60)) / 60)
  const minute = totalMinutes % 60

  if (day > 0) result.day = day
  if (hour > 0) result.hour = hour
  if (minute > 0) result.minute = minute

  return result
}

/**
 * Convert duration object (day, hour, minute) into human-readable string.
 * Example:
 *   { day: 4, hour: 4 } => "4 DAYS 4 HOURS"
 *   { hour: 1, minute: 15 } => "1 HOUR 15 MINUTES"
 */
export const durationToString = (
  duration: {day?: number; hour?: number; minute?: number},
  intl: IntlShape
): string => {
  const parts: string[] = []

  if (duration.day) {
    parts.push(`${duration.day} ${intl.formatMessage({id: textByCount(duration.day, 'D', 'D')})}`)
  }

  if (duration.hour) {
    parts.push(
      `${duration.hour} ${intl.formatMessage({id: textByCount(duration.hour, 'HR', 'HR')})}`
    )
  }

  if (duration.minute) {
    parts.push(
      `${duration.minute} ${intl.formatMessage({
        id: textByCount(duration.minute, 'MIN', 'MIN'),
      })}`
    )
  }

  return parts.join(' ')
}

/**
 * Split a datetime string into { date, time, timezone }
 * @param input Example: '2025-07-19T14:38:00+09:00' or '2025-07-19T14:38'
 * @returns An object with date, time (AM/PM), and timezone (+HH:mm or null)
 */
export const splitDateTime = (input: string) => {
  const m = moment.parseZone(input, moment.ISO_8601, true)
  if (!m.isValid()) {
    return
  }

  return {
    date: m.format(FORMAT_DATE.DATE),
    time: m.format(FORMAT_DATE.TIME_12),
    timezone: m.utcOffset() !== 0 ? m.format('Z') : null,
  }
}
